import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Calendar, Mail, Linkedin, /* Instagram, */ Menu, X, MessageSquare, Trash2 } from 'lucide-react';
import './Sidebar.css';

const Sidebar = ({ 
  onNewConversation, 
  onScheduleCall, 
  onContact, 
  isAnyPopupOpen,
  conversations = [],
  onLoadConversation,
  onRequestDelete,
  currentConversationId,
  isAuthenticated = false
}) => {
  const [isMobileMenuVisible, setIsMobileMenuVisible] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showMobileFade, setShowMobileFade] = useState(true);
  const sidebarRef = useRef(null);
  const toggleButtonRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const animateTimeoutRef = useRef(null);
  const mobileConversationsRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsResizing(true);
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = setTimeout(() => setIsResizing(false), 200);

      // Disable any mobile animation during resize and ensure menu closed on desktop
      if (animateTimeoutRef.current) {
        clearTimeout(animateTimeoutRef.current);
        animateTimeoutRef.current = null;
      }
      setIsAnimating(false);
      if (window.innerWidth > 768 && isMobileMenuVisible) {
        setIsMobileMenuVisible(false);
      }
    };

    const handleCloseSidebar = () => {
      setIsMobileMenuVisible(false);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('closeMobileSidebar', handleCloseSidebar);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('closeMobileSidebar', handleCloseSidebar);
      if (resizeTimeoutRef.current) clearTimeout(resizeTimeoutRef.current);
      if (animateTimeoutRef.current) clearTimeout(animateTimeoutRef.current);
    };
  }, [isMobileMenuVisible]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuVisible) {
        if (
          sidebarRef.current && 
          !sidebarRef.current.contains(event.target) &&
          toggleButtonRef.current && 
          !toggleButtonRef.current.contains(event.target)
        ) {
          setIsMobileMenuVisible(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isMobileMenuVisible]);

  // Basic focus trap when mobile menu is open
  useEffect(() => {
    if (!isMobileMenuVisible) return;
    const root = sidebarRef.current;
    if (!root) return;

    const selectors = [
      'a[href]',
      'button:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ];
    const getFocusable = () => Array.from(root.querySelectorAll(selectors.join(',')));
    const focusables = getFocusable();
    if (focusables.length) {
      focusables[0].focus();
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsMobileMenuVisible(false);
        toggleButtonRef.current?.focus();
      }
      if (e.key === 'Tab') {
        const list = getFocusable();
        if (!list.length) return;
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isMobileMenuVisible]);

  const toggleMobileMenu = () => {
    // Animate only on explicit toggle actions, not during resize
    setIsAnimating(true);
    if (animateTimeoutRef.current) clearTimeout(animateTimeoutRef.current);
    animateTimeoutRef.current = setTimeout(() => setIsAnimating(false), 300);

    setIsMobileMenuVisible((v) => !v);
  };

  const handleItemClick = (callback) => {
    if (callback) {
      callback();
      // Close the mobile sidebar after action
      if (window.innerWidth <= 768) {
        setIsMobileMenuVisible(false);
      }
    } else {
      setIsMobileMenuVisible(false);
    }
  };

  const handleDeleteConversation = (conversationId, conversationTitle, event) => {
    // Prevent all event propagation and default behavior
    event.stopPropagation();
    event.preventDefault();
    
    onRequestDelete(conversationId, conversationTitle);
  };

  // Debounced mobile scroll handler to improve performance
  const handleMobileScroll = useCallback(() => {
    const container = mobileConversationsRef.current;
    if (!container) return;
    
    // Debounce DOM measurements for better scroll performance
    requestAnimationFrame(() => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5; // 5px threshold
      setShowMobileFade(!isAtBottom && conversations.length > 4);
    });
  }, [conversations.length]);

  // Update fade visibility when conversations change
  useEffect(() => {
    if (conversations.length <= 4) {
      setShowMobileFade(false);
    } else {
      setShowMobileFade(true);
    }
  }, [conversations.length]);



  return (
    <>
      <button 
        ref={toggleButtonRef}
        className={`mobile-menu-toggle ${isAnyPopupOpen ? 'disabled' : ''}`}
        onClick={isAnyPopupOpen ? null : toggleMobileMenu}
        disabled={isAnyPopupOpen}
        aria-label={isMobileMenuVisible ? 'Close menu' : 'Open menu'}
        aria-expanded={isMobileMenuVisible}
        aria-controls="mobile-sidebar"
      >
        {isMobileMenuVisible ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile overlay to prevent background interaction */}
      <div
        className={`mobile-overlay ${isMobileMenuVisible ? 'visible' : ''}`}
        onClick={() => setIsMobileMenuVisible(false)}
      />

      <div
        ref={sidebarRef}
        id="mobile-sidebar"
        role="navigation"
        className={`floating-sidebar ${isMobileMenuVisible ? 'mobile-visible' : ''} ${isResizing ? 'is-resizing' : ''} ${isAnimating ? 'mobile-animating' : ''}`}
      >
        <div className="sidebar-content">
          <div className="sidebar-top">
            <div 
              className={`sidebar-item ${!isAuthenticated ? 'disabled' : ''}`} 
              data-tooltip={isAuthenticated ? "New conversation" : "Please sign in to start a new conversation"} 
              onClick={isAuthenticated ? () => handleItemClick(onNewConversation) : undefined}
            >
              <Plus size={20} className="sidebar-icon" />
              <span className="sidebar-item-title">New conversation</span>
            </div>
            
            {/* Conversations Dropdown */}
            <div 
              className={`sidebar-item conversations-dropdown ${!isAuthenticated ? 'disabled' : ''}`} 
              onClick={(e) => e.preventDefault()}
            >
              <MessageSquare size={20} className="sidebar-icon" />
              <span className="sidebar-item-title">Conversations</span>
              
              {/* Desktop Dropdown Menu */}
              <div className="conversations-menu-desktop">
                <div className="conversations-menu-header">
                  <h3 className="conversations-menu-title">Recent Conversations</h3>
                </div>
                {conversations.length > 0 ? (
                  <div className="conversations-menu-list">
                    {conversations.slice(0, 15).map((conv) => (
                      <div
                        key={conv.conversation_id}
                        className={`conversation-menu-item ${
                          currentConversationId === conv.conversation_id ? 'active' : ''
                        } ${!isAuthenticated ? 'disabled' : ''}`}
                        onClick={isAuthenticated ? (e) => {
                          e.stopPropagation();
                          handleItemClick(() => onLoadConversation(conv.conversation_id));
                        } : undefined}
                      >
                        <div className="conversation-menu-content">
                          <div className="conversation-menu-title">{conv.title}</div>
                          <div className="conversation-menu-date">
                            {new Date(conv.last_updated).toLocaleDateString()}
                          </div>
                        </div>
                        {isAuthenticated && (
                          <button
                            className="conversation-delete-btn"
                            onClick={(e) => handleDeleteConversation(conv.conversation_id, conv.title, e)}
                            onMouseDown={(e) => e.stopPropagation()}
                            onMouseUp={(e) => e.stopPropagation()}
                            aria-label={`Delete conversation "${conv.title}"`}
                            title="Delete conversation"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    {conversations.length > 15 && (
                      <div className="conversations-menu-footer">
                        +{conversations.length - 15} more conversations
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="conversations-menu-empty">
                    {isAuthenticated ? (
                      <>
                        <div className="empty-state-text">No conversations yet</div>
                        <div className="empty-state-suggestion">Start a new conversation to get started!</div>
                      </>
                    ) : (
                      <div className="empty-state-text">Please sign in to view conversations</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Conversations List - Always visible on mobile */}
            {conversations.length > 0 && isAuthenticated && (
              <div className="conversations-menu-mobile">
                <div className="mobile-conversations-header">Recent Conversations</div>
                <div className="mobile-conversations-content-container">
                  <div 
                    className="mobile-conversations-content"
                    ref={mobileConversationsRef}
                    onScroll={handleMobileScroll}
                  >
                    {conversations.map((conv) => (
                    <div
                      key={conv.conversation_id}
                      className={`mobile-conversation-item ${
                        currentConversationId === conv.conversation_id ? 'active' : ''
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleItemClick(() => onLoadConversation(conv.conversation_id));
                      }}
                    >
                      <div className="mobile-conversation-content">
                        <div className="mobile-conversation-title">{conv.title}</div>
                        <div className="mobile-conversation-date">
                          {new Date(conv.last_updated).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        className="conversation-delete-btn mobile-delete-btn"
                        onClick={(e) => handleDeleteConversation(conv.conversation_id, conv.title, e)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                        aria-label={`Delete conversation "${conv.title}"`}
                        title="Delete conversation"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                  </div>
                  {showMobileFade && (
                    <div className="conversations-fade-overlay mobile-fade"></div>
                  )}
                </div>
              </div>
            )}
            
            <div 
              className={`sidebar-item ${!isAuthenticated ? 'disabled' : ''}`} 
              data-tooltip={isAuthenticated ? "Schedule a call" : "Please sign in to schedule a call"} 
              onClick={isAuthenticated ? () => handleItemClick(onScheduleCall) : undefined}
            >
              <Calendar size={20} className="sidebar-icon" />
              <span className="sidebar-item-title">Schedule a call</span>
            </div>
            <div 
              className={`sidebar-item ${!isAuthenticated ? 'disabled' : ''}`} 
              data-tooltip={isAuthenticated ? "Reach Qaoud" : "Please sign in to contact Qaoud"} 
              onClick={isAuthenticated ? () => handleItemClick(onContact) : undefined}
            >
              <Mail size={20} className="sidebar-icon" />
              <span className="sidebar-item-title">Reach Qaoud</span>
            </div>
            
            <a 
              href="https://www.linkedin.com/in/mohamedqaoud/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="sidebar-item sidebar-link"
              data-tooltip="LinkedIn"
            >
              <Linkedin size={20} className="sidebar-icon" />
              <span className="sidebar-item-title">LinkedIn</span>
            </a>
            {/* Instagram link - hidden for now, can be uncommented in the future
            <a 
              href="https://www.instagram.com/_qaoud_/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="sidebar-item sidebar-link"
              data-tooltip="Instagram"
            >
              <Instagram size={20} className="sidebar-icon" />
              <span className="sidebar-item-title">Instagram</span>
            </a>
            */}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;